// ContextPad — the hover-anchored action cluster on a process step (BPMN.io's
// context pad; plan §9/§98). The common "add the next thing / restructure this"
// moves are one click away — `+` (add after) and `wrap` — instead of a menu
// dive, with the full ⋯ menu alongside for the rest. Structure-only: every
// action funnels through the shared ProcessStepEditApi (text stays truth).

import React, { useState } from "react";
import type { NodeId } from "@archlang/engine";
import { Button, IconButton } from "@archlang/ui";
import type { ProcessStepEditApi } from "./processStepEdit.js";
import { StepEditMenu } from "./StepEditMenu.js";
import { InlineEditInput } from "./InlineEditInput.js";
import { WRAP_CONSTRUCTS } from "./processConstructs.js";

export interface ContextPadProps {
  readonly api: ProcessStepEditApi;
  readonly nodeId: NodeId;
  /** Passed through to the ⋯ menu — `call` unlocks retype/reassign, etc. */
  readonly kind: "call" | "block" | "other";
  readonly stepIndex?: number;
  readonly from?: string;
  readonly to?: string;
  /** A `call` step's current trailing modifiers — passed to the ⋯ menu's chips. */
  readonly modifiers?: { readonly asName?: string; readonly comment?: string };
}

/** The step context pad. Renders nothing when the api is read-only (capability =
 *  presence — the whole cluster is absent, not a dead affordance). */
export function ContextPad({ api, nodeId, kind, stepIndex, from, to, modifiers }: ContextPadProps): React.JSX.Element | null {
  const [mode, setMode] = useState<null | "add" | "wrap">(null);
  if (!api.canEdit) return null;

  return (
    // Swallow the surface's row-level select/dive click; each action stops its
    // own propagation too.
    <span
      className="inline-flex items-center gap-0.5 rounded-sm border border-slate-700/70 bg-slate-900/90 px-0.5 py-px shadow-sm shadow-black/30"
      data-context-pad=""
      onClick={(e) => e.stopPropagation()}
    >
      {mode === "add" ? (
        <InlineEditInput
          initial=""
          placeholder="A > B"
          ariaLabel="Add step after"
          onCommit={(v) => {
            api.addStepAfter(nodeId, v);
            setMode(null);
          }}
          onCancel={() => setMode(null)}
          className="w-28 box-border rounded-sm border border-sky-500 bg-slate-950 px-1 py-0.5 text-[11px] text-slate-100 outline-none"
        />
      ) : mode === "wrap" ? (
        <span className="flex items-center gap-0.5" data-context-pad-wrap="">
          {WRAP_CONSTRUCTS.map((c) => (
            <Button
              key={c}
              variant="ghost"
              size="xs"
              data-ctx-wrap={c}
              title={`Wrap in ${c}`}
              onClick={() => {
                api.wrap(nodeId, c);
                setMode(null);
              }}
            >
              {c}
            </Button>
          ))}
          <IconButton
            variant="ghost"
            size="xs"
            aria-label="Cancel wrap"
            onClick={() => setMode(null)}
          >
            ×
          </IconButton>
        </span>
      ) : (
        <>
          <IconButton
            variant="ghost"
            size="xs"
            data-ctx-add=""
            title="Add step after"
            aria-label="Add step after"
            onClick={() => setMode("add")}
          >
            +
          </IconButton>
          <IconButton
            variant="ghost"
            size="xs"
            data-ctx-wrap-open=""
            title="Wrap in a construct"
            aria-label="Wrap step"
            onClick={() => setMode("wrap")}
          >
            ⧉
          </IconButton>
          <StepEditMenu api={api} nodeId={nodeId} kind={kind} stepIndex={stepIndex} from={from} to={to} {...(modifiers ? { modifiers } : {})} />
        </>
      )}
    </span>
  );
}
